import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Download, FileSpreadsheet, FileText, FileArchive, Users, Clock, BarChart3, CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const ExportManager = () => {
  const [loadingExport, setLoadingExport] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('excel');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(new Date().getFullYear());

  const downloadExport = async (type, params = {}) => {
    setLoadingExport(type);
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        format: selectedFormat,
        ...params
      }).toString();
      
      const response = await axios.get(
        `${API_URL}/api/export/${type}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Créer le lien de téléchargement
      const blob = new Blob([response.data], {
        type: selectedFormat === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const extension = selectedFormat === 'excel' ? 'xlsx' : 'csv';
      link.download = `${type}_${date}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${type} téléchargé avec succès`);
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'export');
    } finally {
      setLoadingExport(null);
    }
  };

  // Les fiches de suivi sont des documents Word : ni le sélecteur de format
  // Excel/CSV ni le nommage ci-dessus ne s'y appliquent.
  const downloadFiches = async () => {
    setLoadingExport('fiches');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/export/fiches-beneficiaires?inclure_sortis=true`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Fiches_suivi_beneficiaires_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Fiches de suivi téléchargées');
    } catch (error) {
      console.error('Erreur export fiches:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération des fiches');
    } finally {
      setLoadingExport(null);
    }
  };

  const exportCards = [
    {
      type: 'beneficiaires',
      title: 'Liste des Bénéficiaires',
      description: 'Exporter tous les bénéficiaires avec leurs informations (nom, contact, zone, freins...)',
      icon: Users,
      color: 'bg-blue-500',
      params: {}
    },
    {
      type: 'emargements',
      title: 'Feuilles d\'Émargement',
      description: 'Exporter les présences et signatures des ateliers',
      icon: CalendarDays,
      color: 'bg-green-500',
      params: () => ({
        ...(dateDebut && { date_debut: dateDebut }),
        ...(dateFin && { date_fin: dateFin })
      })
    },
    {
      type: 'statistiques',
      title: 'Statistiques Globales',
      description: 'Exporter les indicateurs clés (bénéficiaires actifs, contrats, ateliers...)',
      icon: BarChart3,
      color: 'bg-purple-500',
      params: () => ({ mois, annee })
    },
    {
      type: 'heures-beneficiaires',
      title: 'Heures par Bénéficiaire',
      description: 'Exporter le détail des heures consommées par bénéficiaire (ateliers, RDV, activités)',
      icon: Clock,
      color: 'bg-orange-500',
      params: {}
    }
  ];

  return (
    <div className="space-y-6">
      {/* Fiches de suivi individuel pour le financeur */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-blue-600" />
            Fiches de suivi individuel (Word)
          </CardTitle>
          <CardDescription>
            Une fiche Word par bénéficiaire — profil, diagnostic, projet d'emploi, freins,
            heures signées, entretiens, ateliers, candidatures et sortie — accompagnée d'un
            tableau de synthèse Excel. Destinée au financeur.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-slate-500 max-w-xl">
              La génération prend quelques instants : chaque fiche est reconstituée à partir des
              données à jour. Les heures affichées ne comptent que les présences signées.
            </p>
            <Button
              onClick={downloadFiches}
              disabled={loadingExport === 'fiches'}
              className="gap-2"
              data-testid="export-fiches-btn"
            >
              {loadingExport === 'fiches'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Download className="h-4 w-4" />}
              {loadingExport === 'fiches' ? 'Génération...' : 'Télécharger les fiches (ZIP)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export de Données
          </CardTitle>
          <CardDescription>
            Téléchargez les données de la plateforme au format Excel ou CSV
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Sélecteur de format global */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
            <Label className="font-medium">Format d'export :</Label>
            <div className="flex gap-2">
              <Button
                variant={selectedFormat === 'excel' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFormat('excel')}
                className="gap-2"
                data-testid="format-excel-btn"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excel (.xlsx)
              </Button>
              <Button
                variant={selectedFormat === 'csv' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFormat('csv')}
                className="gap-2"
                data-testid="format-csv-btn"
              >
                <FileText className="h-4 w-4" />
                CSV
              </Button>
            </div>
          </div>

          {/* Filtres de période */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700">Filtres pour Émargements</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date début</Label>
                  <input
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    data-testid="date-debut-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date fin</Label>
                  <input
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    data-testid="date-fin-input"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-700">Filtres pour Statistiques</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mois</Label>
                  <Select value={String(mois)} onValueChange={(v) => setMois(parseInt(v))}>
                    <SelectTrigger className="mt-1" data-testid="mois-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MOIS.map((m, i) => (
                        <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Année</Label>
                  <Select value={String(annee)} onValueChange={(v) => setAnnee(parseInt(v))}>
                    <SelectTrigger className="mt-1" data-testid="annee-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((a) => (
                        <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Cartes d'export */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {exportCards.map((card) => {
              const Icon = card.icon;
              const isLoading = loadingExport === card.type;
              const params = typeof card.params === 'function' ? card.params() : card.params;
              
              return (
                <Card key={card.type} className="border hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${card.color}`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{card.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{card.description}</p>
                        <Button
                          className="mt-3 gap-2"
                          size="sm"
                          onClick={() => downloadExport(card.type, params)}
                          disabled={isLoading}
                          data-testid={`export-${card.type}-btn`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Export en cours...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4" />
                              Télécharger
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportManager;
